/**
 * Notification API client for frontend
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

/**
 * Get authentication headers for API requests
 */
const getAuthHeaders = async () => {
  const accessToken = localStorage.getItem("access_token");
  
  if (!accessToken) {
    throw new Error("User not authenticated");
  }
  
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
};

/**
 * Fetch user notifications with optional filters and pagination
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (unread, read)
 * @param {string} options.type - Filter by type (brand_approval, etc.)
 * @param {string} options.entityType - Filter by entity type (brand, cause, campaign)
 * @param {string} options.startDate - Filter by start date
 * @param {string} options.endDate - Filter by end date
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.sortBy - Sort field (default: createdAt)
 * @param {string} options.sortOrder - Sort order (asc/desc, default: desc)
 * @returns {Promise<Object>} API response with notifications and pagination
 */
export const fetchNotifications = async (options = {}) => {
  try {
    const {
      status,
      type,
      types,
      sender,
      entityType,
      startDate,
      endDate,
      includeArchived = false,
      page = 1,
      limit = 20,
      sortBy = 'createdAt',
      sortOrder = 'desc',
    } = options;

    const queryParams = new URLSearchParams();

    if (status) queryParams.append('status', status);
    if (type) queryParams.append('type', type);
    if (types && Array.isArray(types) && types.length > 0) {
      queryParams.append('types', types.join(','));
    }
    if (sender) queryParams.append('sender', sender);
    if (entityType) queryParams.append('entityType', entityType);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (includeArchived) queryParams.append('includeArchived', 'true');
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    queryParams.append('sortBy', sortBy);
    queryParams.append('sortOrder', sortOrder);

    const headers = await getAuthHeaders();
    
    const response = await fetch(
      `${API_BASE_URL}/api/notifications?${queryParams.toString()}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch notifications');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count
 * @returns {Promise<number>} Unread notification count
 */
export const getUnreadCount = async () => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/notifications/unread-count`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();

      // If token is invalid, return 0 instead of throwing
      // This prevents errors during session transitions (like password changes)
      if (response.status === 401 || errorData.message?.includes('Invalid token')) {
        console.warn('Invalid token detected in getUnreadCount, returning 0');
        return 0;
      }

      throw new Error(errorData.message || 'Failed to fetch unread count');
    }

    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

/**
 * Mark a specific notification as read
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} API response
 */
export const markAsRead = async (notificationId) => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(
      `${API_BASE_URL}/api/notifications/${notificationId}/read`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to mark notification as read');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} API response with modified count
 */
export const markAllAsRead = async () => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(
      `${API_BASE_URL}/api/notifications/mark-all-read`,
      {
        method: 'POST',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to mark all notifications as read');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} API response
 */
export const deleteNotification = async (notificationId) => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/notifications/${notificationId}`,
      {
        method: 'DELETE',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete notification');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Get a single notification details by ID
 * @param {string} notificationId - The notification ID
 * @returns {Promise<Object>} API response with notification details
 */
export const getNotificationDetails = async (notificationId) => {
  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}/api/notifications/${notificationId}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch notification details');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching notification details:', error);
    throw error;
  }
};

/**
 * Helper function to format notification for display
 * @param {Object} notification - Raw notification object
 * @returns {Object} Formatted notification object
 */
export const formatNotification = (notification) => {
  // Enhanced description that includes rejection reason if available
  let enhancedDescription = notification.message;
  
  // For rejection notifications, append the rejection reason if available
  if (notification.type.includes('rejection') && notification.metadata?.rejectionReason) {
    enhancedDescription += `\n\nReason: ${notification.metadata.rejectionReason}`;
  }

  return {
    id: notification._id,
    title: notification.title,
    message: notification.message,
    type: notification.type,
    entityType: notification.entityType,
    entityName: notification.entityName,
    entityId: notification.entityId,
    status: notification.status,
    createdAt: notification.createdAt,
    updatedAt: notification.updatedAt,
    metadata: notification.metadata,

    // Add computed fields for UI
    time: formatTimeAgo(notification.createdAt),
    sender: notification.sender || getNotificationSender(notification.type),
    description: enhancedDescription,
    image: getNotificationImage(notification.type, notification.entityType),
    typeBadge: getNotificationTypeBadge(notification.type),

    // Add rejection reason for easy access
    rejectionReason: notification.metadata?.rejectionReason || null,

    // Add flag for actionable notifications
    requiresAction: notification.metadata?.requiresAction || false,
  };
};

/**
 * Format time ago string
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time ago
 */
const formatTimeAgo = (dateString) => {
  if (!dateString) {
    return 'Unknown time';
  }

  const date = new Date(dateString);
  const now = new Date();

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid date string:', dateString);
    return 'Invalid date';
  }

  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);

  // Handle future dates (clock skew)
  if (diffInMs < 0) {
    return 'Just now';
  }

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Get sender name based on notification type
 * @param {string} type - Notification type
 * @returns {string} Sender name
 */
const getNotificationSender = (type) => {
  if (type.includes('approval') || type.includes('rejection')) {
    return 'Impactoverse';
  }
  return 'System';
};

/**
 * Get notification type badge info
 * @param {string} type - Notification type
 * @returns {Object} Badge info with label and color
 */
const getNotificationTypeBadge = (type) => {
  const typeMap = {
    brand_approval: { label: 'Approval', color: 'green' },
    brand_rejection: { label: 'Rejection', color: 'red' },
    cause_approval: { label: 'Approval', color: 'green' },
    cause_rejection: { label: 'Rejection', color: 'red' },
    campaign_approval: { label: 'Approval', color: 'green' },
    campaign_rejection: { label: 'Rejection', color: 'red' },
    campaign_milestone_approval: { label: 'Approval', color: 'green' },
    campaign_milestone_rejection: { label: 'Rejection', color: 'red' },
    campaign_post: { label: 'Update', color: 'gray' },
    comment: { label: 'Comment', color: 'blue' },
    mention: { label: 'Mention', color: 'purple' },
    reminder: { label: 'Reminder', color: 'amber' },
    update: { label: 'Update', color: 'gray' },
    sub_campaign_request: { label: 'Request', color: 'indigo' },
    rejection: { label: 'Rejection', color: 'red' },
  };

  return typeMap[type] || { label: 'Notification', color: 'gray' };
};

/**
 * Get notification image based on type and entity
 * @param {string} type - Notification type
 * @param {string} entityType - Entity type
 * @returns {string} Image URL or path
 */
const getNotificationImage = (type, entityType) => {
  if (type.includes('approval')) {
    return '/images/notifications/approval.svg';
  } else if (type.includes('rejection')) {
    return '/images/notifications/rejection.svg';
  } else if (entityType === 'brand') {
    return '/images/notifications/brand.svg';
  } else if (entityType === 'cause') {
    return '/images/notifications/cause.svg';
  } else if (entityType === 'campaign') {
    return '/images/notifications/campaign.svg';
  }
  return '/images/notifications/default.svg';
};

/**
 * Get all notification type options for filtering
 * @returns {Array} Array of type options
 */
export const getNotificationTypeOptions = () => {
  return [
    { value: 'brand_approval', label: 'Approval', badge: 'Approval', color: 'green' },
    { value: 'brand_rejection', label: 'Rejection', badge: 'Rejection', color: 'red' },
    { value: 'cause_approval', label: 'Approval', badge: 'Approval', color: 'green' },
    { value: 'cause_rejection', label: 'Rejection', badge: 'Rejection', color: 'red' },
    { value: 'campaign_approval', label: 'Approval', badge: 'Approval', color: 'green' },
    { value: 'campaign_rejection', label: 'Rejection', badge: 'Rejection', color: 'red' },
    { value: 'comment', label: 'Comment', badge: 'Comment', color: 'blue' },
    { value: 'mention', label: 'Mention', badge: 'Mention', color: 'purple' },
    { value: 'reminder', label: 'Reminder', badge: 'Reminder', color: 'amber' },
    { value: 'update', label: 'Update', badge: 'Update', color: 'gray' },
    { value: 'sub_campaign_request', label: 'Request', badge: 'Request', color: 'indigo' },
  ];
};

/**
 * Get unique badge types from type options (for filter dropdown)
 * @returns {Array} Array of unique badge types
 */
export const getUniqueBadgeTypes = () => {
  return [
    { value: 'approval', label: 'Approval', color: 'green' },
    { value: 'rejection', label: 'Rejection', color: 'red' },
    { value: 'comment', label: 'Comment', color: 'blue' },
    { value: 'mention', label: 'Mention', color: 'purple' },
    { value: 'reminder', label: 'Reminder', color: 'amber' },
    { value: 'update', label: 'Update', color: 'gray' },
    { value: 'request', label: 'Request', color: 'indigo' },
  ];
};